import type { PayslipFields } from './payslip';
import { MANDATORY_DETAILS, NOT_READ_FIELDS } from './payslip';
import { isIsoDate, isYearMonth } from './dates';

export type ParseResult = { ok: true; fields: PayslipFields } | { ok: false; errors: string[] };

const MONEY = [
  'hourlyRate', 'baseSalary', 'travelAmount', 'recuperationAmount', 'grossPay', 'grossForTax', 'grossForNI', 'incomeTax',
  'nationalInsurance', 'healthTax', 'pensionEmployee', 'pensionEmployer', 'severanceEmployer', 'educationFundEmployee',
  'educationFundEmployer', 'imputedBenefits', 'netPay', 'netToBank', 'foreignDeposit',
] as const;
const NUMBERS = ['hoursWorked', 'hoursPaid', 'hoursActual', 'standardHours', 'standardDays', 'daysWorked', 'recuperationDays', 'marginalTaxRate', 'creditPoints', 'unreadablePaymentLines'] as const;
const STRINGS = ['employerName', 'employerCompanyId', 'deductionsFileNumber', 'pensionFundName'] as const;
const OBJECTS = ['vacationBalance', 'sickBalance', 'ytd'] as const;
const KINDS = new Set([
  'base', 'fixed', 'overtime', 'globalOvertime', 'shift', 'bonus', 'seniority', 'expense', 'travel', 'recuperation', 'vacationCashOut',
  'sickPay', 'holidayPay', 'reserveDuty', 'backPay', 'severancePay', 'noticePay', 'commission', 'other', 'unknown',
]);
const KNOWN = new Set<string>([
  ...MONEY, ...NUMBERS, ...STRINGS, ...OBJECTS, 'payMonth', 'startDate', 'seniorityDate', 'payBasis', 'jobPercent', 'paymentLines', 'overtimeLines', 'unreadablePaymentLines',
  'printedMinimumWage', 'mandatoryDetails', 'notRead', 'deductionLines', 'printedLabels', 'taxableValueLines',
]);
const PRINTED_LABELS = new Set(['incomeTax', 'nationalInsurance', 'healthTax', 'pensionEmployee', 'educationFundEmployee']);
const DEDUCTION_KINDS = new Set(['housing', 'ancillary', 'healthInsurance', 'food', 'depositEmployee', 'other', 'unknown']);

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isAgorot = (v: unknown) => typeof v === 'number' && Number.isSafeInteger(v);
const isNumber = (v: unknown) => typeof v === 'number' && Number.isFinite(v);

/**
 * Checks hand-entered JSON against the PayslipFields shape. Strict on purpose:
 * unknown keys (typos) and non-integer money (shekels instead of agorot) are errors,
 * because a silently ignored field would give a wrong result.
 */
export function parsePayslipFields(value: unknown): ParseResult {
  if (!isObject(value)) return { ok: false, errors: ['The payslip must be a JSON object.'] };
  const errors: string[] = [];
  const v = value;

  for (const key of Object.keys(v)) if (!KNOWN.has(key)) errors.push(`Unknown field "${key}".`);
  for (const key of MONEY) if (key in v && !isAgorot(v[key])) errors.push(`${key} must be a whole number of agorot.`);
  for (const key of NUMBERS) if (key in v && !isNumber(v[key])) errors.push(`${key} must be a number.`);
  for (const key of STRINGS) if (key in v && typeof v[key] !== 'string') errors.push(`${key} must be text.`);
  for (const key of OBJECTS) if (key in v && !isObject(v[key])) errors.push(`${key} must be an object.`);

  if ('payMonth' in v && !(typeof v.payMonth === 'string' && isYearMonth(v.payMonth))) errors.push('payMonth must be YYYY-MM.');
  if ('startDate' in v && !(typeof v.startDate === 'string' && isIsoDate(v.startDate))) errors.push('startDate must be YYYY-MM-DD.');
  if ('seniorityDate' in v && !(typeof v.seniorityDate === 'string' && isIsoDate(v.seniorityDate))) errors.push('seniorityDate must be YYYY-MM-DD.');
  if ('jobPercent' in v && !(isNumber(v.jobPercent) && (v.jobPercent as number) > 0 && (v.jobPercent as number) <= 100)) {
    errors.push('jobPercent must be a number above 0 and up to 100.');
  }
  if ('payBasis' in v) {
    const p = v.payBasis;
    if (!(isObject(p) && (p.value === 'monthly' || p.value === 'hourly') && typeof p.inferred === 'boolean')) {
      errors.push('payBasis must be {"value": "monthly" | "hourly", "inferred": true | false}.');
    }
  }
  if ('paymentLines' in v) {
    if (!Array.isArray(v.paymentLines)) errors.push('paymentLines must be a list.');
    else {
      v.paymentLines.forEach((line: unknown, i) => {
        const where = `paymentLines[${i}]`;
        if (!isObject(line)) return void errors.push(`${where} must be an object.`);
        if (typeof line.code !== 'string' || typeof line.label !== 'string') errors.push(`${where} needs text "code" and "label".`);
        if (!isAgorot(line.amount)) errors.push(`${where}.amount must be a whole number of agorot.`);
        if ('kind' in line && !KINDS.has(String(line.kind))) errors.push(`${where}.kind "${String(line.kind)}" is not a known kind.`);
        if ('rate' in line && !isAgorot(line.rate)) errors.push(`${where}.rate must be a whole number of agorot.`);
        if ('quantity' in line && !isNumber(line.quantity)) errors.push(`${where}.quantity must be a number.`);
        if ('periodOverride' in line && !(typeof line.periodOverride === 'string' && isYearMonth(line.periodOverride))) {
          errors.push(`${where}.periodOverride must be YYYY-MM.`);
        }
      });
    }
  }
  if ('overtimeLines' in v && !Array.isArray(v.overtimeLines)) errors.push('overtimeLines must be a list.');
  if ('printedLabels' in v && (!isObject(v.printedLabels) || Object.entries(v.printedLabels).some(([k, l]) => !PRINTED_LABELS.has(k) || typeof l !== 'string'))) {
    errors.push(`printedLabels must map ${[...PRINTED_LABELS].join(', ')} to text.`);
  }
  if ('deductionLines' in v) {
    if (!Array.isArray(v.deductionLines)) errors.push('deductionLines must be a list.');
    else {
      v.deductionLines.forEach((line: unknown, i) => {
        const where = `deductionLines[${i}]`;
        if (!isObject(line)) return void errors.push(`${where} must be an object.`);
        if (typeof line.label !== 'string') errors.push(`${where} needs a text "label".`);
        if (!DEDUCTION_KINDS.has(String(line.kind))) errors.push(`${where}.kind "${String(line.kind)}" is not a known kind.`);
        if (!isAgorot(line.amount)) errors.push(`${where}.amount must be a whole number of agorot.`);
      });
    }
  }
  if ('taxableValueLines' in v) {
    if (!Array.isArray(v.taxableValueLines)) errors.push('taxableValueLines must be a list.');
    else {
      v.taxableValueLines.forEach((line: unknown, i) => {
        const where = `taxableValueLines[${i}]`;
        if (!isObject(line)) return void errors.push(`${where} must be an object.`);
        if (typeof line.code !== 'string' || typeof line.label !== 'string') errors.push(`${where} needs a text "code" and "label".`);
        if (!isAgorot(line.value)) errors.push(`${where}.value must be a whole number of agorot.`);
      });
    }
  }
  if ('mandatoryDetails' in v) {
    const m = v.mandatoryDetails;
    const list = (x: unknown) => Array.isArray(x) && x.every((d) => (MANDATORY_DETAILS as readonly unknown[]).includes(d));
    if (!isObject(m) || !list(m.checked) || !list(m.absent)) errors.push('mandatoryDetails must be {"checked": [...], "absent": [...]} of known detail names.');
  }
  if ('notRead' in v && !(Array.isArray(v.notRead) && v.notRead.every((d) => (NOT_READ_FIELDS as readonly unknown[]).includes(d)))) {
    errors.push(`notRead must be a list of: ${NOT_READ_FIELDS.join(', ')}.`);
  }
  if ('printedMinimumWage' in v) {
    const m = v.printedMinimumWage;
    if (!isObject(m) || Object.keys(m).some((k) => k !== 'monthly' && k !== 'hourly') || Object.values(m).some((x) => !isAgorot(x))) {
      errors.push('printedMinimumWage must be {"monthly"?: agorot, "hourly"?: agorot}.');
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true, fields: v as PayslipFields };
}
